import { API_URL } from '@/config';
import React, { useState, useEffect } from 'react';
import { Settings, Globe, Palette, Mail, Database, Shield, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const SystemSettings = ({ i18n }) => {
  const [settings, setSettings] = useState({
    siteName: 'DriveMaster',
    siteNameAr: 'ماستر القيادة',
    siteDescription: 'Master your driving skills',
    siteDescriptionAr: 'أتقن مهارات القيادة',
    defaultLanguage: 'en',
    enableRegistration: true,
    enableEmailVerification: false,
    maintenanceMode: false,
    analyticsEnabled: true,
    currency: 'USD',
    timezone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
    themeColor: 'emerald',
    contactEmail: 'drivemaster.se@gmail.com',
    contactPhone: '+1234567890',
    supportHours: '24/7'
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/system-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error(i18n.language === 'ar' ? 'فشل تحميل الإعدادات' : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/system-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to save settings');
      }

      toast.success(i18n.language === 'ar' ? 'تم حفظ الإعدادات! ✅' : 'Settings saved! ✅');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(i18n.language === 'ar' ? `فشل حفظ الإعدادات: ${error.message}` : `Failed to save settings: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (key) => {
    setSettings({ ...settings, [key]: !settings[key] });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl p-6 border border-slate-200">
        <h3 className="text-2xl font-bold text-slate-900 flex items-center gap-2 mb-6">
          <Settings className="w-7 h-7 text-slate-600" />
          {i18n.language === 'ar' ? 'إعدادات النظام' : 'System Settings'}
        </h3>

        {/* General Settings */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-5 h-5 text-blue-600" />
            <h4 className="text-lg font-bold text-slate-900">
              {i18n.language === 'ar' ? 'الإعدادات العامة' : 'General Settings'}
            </h4>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>{i18n.language === 'ar' ? 'اسم الموقع (EN)' : 'Site Name (EN)'}</Label>
                <Input
                  value={settings.siteName}
                  onChange={(e) => setSettings({ ...settings, siteName: e.target.value })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>{i18n.language === 'ar' ? 'اسم الموقع (AR)' : 'Site Name (AR)'}</Label>
                <Input
                  value={settings.siteNameAr}
                  onChange={(e) => setSettings({ ...settings, siteNameAr: e.target.value })}
                  className="mt-2"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>{i18n.language === 'ar' ? 'وصف الموقع (EN)' : 'Site Description (EN)'}</Label>
                <Input
                  value={settings.siteDescription}
                  onChange={(e) => setSettings({ ...settings, siteDescription: e.target.value })}
                  className="mt-2"
                />
              </div>
              <div>
                <Label>{i18n.language === 'ar' ? 'وصف الموقع (AR)' : 'Site Description (AR)'}</Label>
                <Input
                  value={settings.siteDescriptionAr}
                  onChange={(e) => setSettings({ ...settings, siteDescriptionAr: e.target.value })}
                  className="mt-2"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>{i18n.language === 'ar' ? 'اللغة الافتراضية' : 'Default Language'}</Label>
                <Select value={settings.defaultLanguage} onValueChange={(val) => setSettings({ ...settings, defaultLanguage: val })}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sv">🇸🇪 Svenska</SelectItem>
                    <SelectItem value="en">🇬🇧 English</SelectItem>
                    <SelectItem value="ar">🇸🇦 العربية</SelectItem>
                    <SelectItem value="tr">🇹🇷 Türkçe</SelectItem>
                    <SelectItem value="fa">🇮🇷 فارسی</SelectItem>
                    <SelectItem value="so">🇸🇴 Soomaali</SelectItem>
                    <SelectItem value="pl">🇵🇱 Polski</SelectItem>
                    <SelectItem value="es">🇪🇸 Español</SelectItem>
                    <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{i18n.language === 'ar' ? 'العملة' : 'Currency'}</Label>
                <Select value={settings.currency} onValueChange={(val) => setSettings({ ...settings, currency: val })}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">$ USD</SelectItem>
                    <SelectItem value="EUR">€ EUR</SelectItem>
                    <SelectItem value="SEK">kr SEK</SelectItem>
                    <SelectItem value="SAR">ر.س SAR</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{i18n.language === 'ar' ? 'المنطقة الزمنية' : 'Timezone'}</Label>
                <Select value={settings.timezone} onValueChange={(val) => setSettings({ ...settings, timezone: val })}>
                  <SelectTrigger className="mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="UTC">UTC</SelectItem>
                    <SelectItem value="America/New_York">EST (New York)</SelectItem>
                    <SelectItem value="Europe/Stockholm">CET (Stockholm)</SelectItem>
                    <SelectItem value="Asia/Dubai">GST (Dubai)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {/* Appearance */}
        <div className="mb-6 border-t border-slate-200 pt-6">
          <div className="flex items-center gap-2 mb-4">
            <Palette className="w-5 h-5 text-purple-600" />
            <h4 className="text-lg font-bold text-slate-900">
              {i18n.language === 'ar' ? 'المظهر' : 'Appearance'}
            </h4>
          </div>
          <div>
            <Label>{i18n.language === 'ar' ? 'لون الموقع' : 'Theme Color'}</Label>
            <Select value={settings.themeColor} onValueChange={(val) => setSettings({ ...settings, themeColor: val })}>
              <SelectTrigger className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="emerald">🟢 Emerald</SelectItem>
                <SelectItem value="blue">🔵 Blue</SelectItem>
                <SelectItem value="purple">🟣 Purple</SelectItem>
                <SelectItem value="red">🔴 Red</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Contact Information */}
        <div className="mb-6 border-t border-slate-200 pt-6">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-5 h-5 text-teal-600" />
            <h4 className="text-lg font-bold text-slate-900">
              {i18n.language === 'ar' ? 'معلومات الاتصال' : 'Contact Information'}
            </h4>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>{i18n.language === 'ar' ? 'البريد الإلكتروني' : 'Contact Email'}</Label>
              <Input
                type="email"
                value={settings.contactEmail}
                onChange={(e) => setSettings({ ...settings, contactEmail: e.target.value })}
                className="mt-2"
              />
            </div>
            <div>
              <Label>{i18n.language === 'ar' ? 'رقم الهاتف' : 'Contact Phone'}</Label>
              <Input
                type="tel"
                value={settings.contactPhone}
                onChange={(e) => setSettings({ ...settings, contactPhone: e.target.value })}
                className="mt-2"
              />
            </div>
            <div>
              <Label>{i18n.language === 'ar' ? 'ساعات الدعم' : 'Support Hours'}</Label>
              <Input
                value={settings.supportHours}
                onChange={(e) => setSettings({ ...settings, supportHours: e.target.value })}
                className="mt-2"
                placeholder="24/7"
              />
            </div>
          </div>
        </div>

        {/* Feature Toggles */}
        <div className="mb-6 border-t border-slate-200 pt-6">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="w-5 h-5 text-indigo-600" />
            <h4 className="text-lg font-bold text-slate-900">
              {i18n.language === 'ar' ? 'الميزات' : 'Features'}
            </h4>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <div className="font-medium text-slate-900">{i18n.language === 'ar' ? 'السماح بالتسجيل' : 'Allow Registration'}</div>
                <div className="text-sm text-slate-500">{i18n.language === 'ar' ? 'السماح للمستخدمين بإنشاء حسابات' : 'Allow users to create accounts'}</div>
              </div>
              <Switch checked={settings.enableRegistration} onCheckedChange={() => handleToggle('enableRegistration')} />
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <div className="font-medium text-slate-900">{i18n.language === 'ar' ? 'تفعيل التحليلات' : 'Analytics Enabled'}</div>
                <div className="text-sm text-slate-500">{i18n.language === 'ar' ? 'تتبع زيارات الموقع' : 'Track site visits and user behavior'}</div>
              </div>
              <Switch checked={settings.analyticsEnabled} onCheckedChange={() => handleToggle('analyticsEnabled')} />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-3 border-t border-slate-200 pt-6">
          <Button variant="outline" onClick={loadSettings} disabled={saving}>
            {i18n.language === 'ar' ? 'إعادة تعيين' : 'Reset'}
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 me-2 animate-spin" />
                {i18n.language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}
              </>
            ) : (
              <>
                <Database className="w-4 h-4 me-2" />
                {i18n.language === 'ar' ? 'حفظ الإعدادات' : 'Save Settings'}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;